import React, { useEffect, useState, createElement } from 'react';
import { Navbar } from '../components/Navbar';
import { HeroSection } from '../components/HeroSection';
import { HeroSecondaryVisual } from '../components/HeroSecondaryVisual';
import { ManifestoSection } from '../components/ManifestoSection';
import { MethodSection } from '../components/MethodSection';
import { ProCreditWatchSection } from '../components/ProCreditWatchSection';
import { ComparisonTableSection } from '../components/ComparisonTableSection';
import { BureauTrustSection } from '../components/BureauTrustSection';
import { LegalAuthoritySection } from '../components/LegalAuthoritySection';
import { FAQSection } from '../components/FAQSection';
import { FinalCTASection } from '../components/FinalCTASection';
import { Footer } from '../components/Footer';
import { LeadFormModal } from '../components/LeadFormModal';
import { ExitIntentModal } from '../components/ExitIntentModal';
import { SectionDivider } from '../components/SectionDivider';
import { ArrowRight } from 'lucide-react';
import { trackIntent } from '../utils/metaPixel';
export function LandingPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'full' | 'notify'>('full');
  const [resourceTitle, setResourceTitle] = useState('');
  const handleOpenForm = () => {
    setModalMode('full');
    setIsModalOpen(true);
  };
  const handleOpenNotifyForm = (title: string) => {
    setModalMode('notify');
    setResourceTitle(title);
    setIsModalOpen(true);
  };
  const handleCloseForm = () => setIsModalOpen(false);
  // SEO and Tracking Placeholders via useEffect
  useEffect(() => {
    document.title = 'The Credit Bureau Data Most Consumers Never Audit | Blinkblis';
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    setMetaTag('description', 'Learn the 2-step method to rebuild credit. Add positive accounts and legally remove errors using FCRA Section 611.');
    setMetaTag('og:title', 'Blinkblis | Rebuild Credit The Honest Way', true);
    setMetaTag('og:description', 'Learn the 2-step method to rebuild credit. Add positive accounts and legally remove errors using FCRA Section 611.', true);
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:url', 'https://blinkblis.com', true);
    setMetaTag('og:image', 'https://blinkblis.com/og-image.png', true);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://blinkblis.com');
    // JSON-LD Schema Injection
    const schemaId = 'blinkblis-schema-jsonld';
    let schemaScript = document.getElementById(schemaId);
    if (schemaScript) {
      schemaScript.remove();
    }
    schemaScript = document.createElement('script');
    schemaScript.id = schemaId;
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [{
        '@type': 'WebSite',
        name: 'Blinkblis',
        url: 'https://blinkblis.com'
      }, {
        '@type': 'Organization',
        name: 'Blinkblis Editorial Team',
        url: 'https://blinkblis.com',
        description: 'Editorial research platform analyzing consumer credit reporting discrepancies, CFPB complaint patterns, and FCRA-related reporting issues.'
      }, {
        '@type': 'FAQPage',
        mainEntity: [{
          '@type': 'Question',
          name: 'Why do all three bureaus show different information?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Bureaus receive reporting updates at different times from different furnishers. Complaint records show timing inconsistencies and incomplete synchronization are common across consumer files.'
          }
        }, {
          '@type': 'Question',
          name: "How does Blinkblis make money if it's free to read?",
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Blinkblis earns affiliate revenue from selected financial tools while maintaining editorial independence and public-source research standards.'
          }
        }, {
          '@type': 'Question',
          name: "Doesn't monitoring just create awareness, not solutions?",
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Correct. Monitoring creates visibility. Consumers still review, document, and submit disputes independently under federal law.'
          }
        }]
      }]
    });
    document.head.appendChild(schemaScript);
    let impactMeta = document.querySelector('meta[name="impact-site-verification"]');
    if (!impactMeta) {
      impactMeta = document.createElement('meta');
      impactMeta.setAttribute('name', 'impact-site-verification');
      document.head.appendChild(impactMeta);
    }
    impactMeta.setAttribute('value', '8065a18c-1fc2-4b6f-ab4a-65da7763db56');
    // Impact.com tracking script (UTT) — verifies ownership AND tracks link clicks
    // Property ID: P-A5321819-0c8a-4ae7-b62c-bb9ca1d8af9a1
    if (!document.querySelector('script[data-impact-utt]')) {
      ;
      (function (i: string, m: string, p: string, a: Document, c: any, t?: any) {
        c.ire_o = p;
        c[p] = c[p] || function () {
          ;
          (c[p].a = c[p].a || []).push(arguments);
        };
        t = a.createElement(m);
        var z = a.getElementsByTagName(m)[0];
        t.async = 1;
        t.src = i;
        t.setAttribute('data-impact-utt', 'true');
        if (z && z.parentNode) {
          z.parentNode.insertBefore(t, z);
        } else {
          ;
          (a.head || a.documentElement).appendChild(t);
        }
      })('https://utt.impactcdn.com/P-A5321819-0c8a-4ae7-b62c-bb9ca1d8af9a1.js', 'script', 'impactStat', document, window);
      (window as any).impactStat('transformLinks');
      (window as any).impactStat('trackImpression');
    }
    ;
    (window as any).ga4Id = 'GA4_ID_PLACEHOLDER';
  }, []);
  return <div className="min-h-screen bg-white font-sans selection:bg-emerald selection:text-white" data-id="element-593">
      <Navbar onOpenForm={handleOpenForm} data-id="element-594" />

      <main className="pb-24 md:pb-0" data-id="element-595">
        <HeroSection onOpenForm={handleOpenForm} data-id="element-596" />

        <HeroSecondaryVisual data-id="element-597" />

        <SectionDivider label="~ EDITORIAL ANALYSIS ~" data-id="element-598" />
        <ManifestoSection data-id="element-599" />

        <MethodSection onOpenForm={handleOpenForm} data-id="element-600" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-601">
          <p className="font-serif italic text-base text-slate-600 text-center leading-[1.7]" data-id="element-602">
            Bureau errors are technically "verified" more often than consumers
            realize. Once you see why, the entire industry starts looking
            different.
          </p>
        </div>

        <SectionDivider label="~ INFRASTRUCTURE REVIEW ~" data-id="element-603" />
        <ProCreditWatchSection data-id="element-604" />
        <ComparisonTableSection data-id="element-605" />
        <BureauTrustSection data-id="element-606" />

        <SectionDivider label="~ FEDERAL AUTHORITY ~" data-id="element-607" />
        <LegalAuthoritySection onOpenForm={handleOpenForm} data-id="element-608" />

        <FAQSection data-id="element-609" />
        <FinalCTASection onOpenForm={handleOpenForm} data-id="element-610" />
      </main>

      <Footer onOpenForm={handleOpenForm} data-id="element-611" />

      <LeadFormModal isOpen={isModalOpen} onClose={handleCloseForm} mode={modalMode} resourceTitle={resourceTitle} data-id="element-612" />
      <ExitIntentModal data-id="element-613" />

      {/* Sticky Mobile CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-md border-t border-gray-200 z-30" data-id="element-614">
        <button onClick={() => {
        trackIntent('sticky_mobile_playbook');
        handleOpenForm();
      }} className="w-full bg-emerald hover:bg-emerald-600 text-white px-6 py-3.5 rounded-lg font-bold text-base transition-colors shadow-cta flex items-center justify-center" data-id="element-615">
          Get The Playbook
          <ArrowRight className="ml-2 w-4 h-4" data-id="element-616" />
        </button>
      </div>
    </div>;
}